import React, { useEffect, useMemo, useState } from 'react';
import { BelowMapSetter } from '../layouts/BelowMapContext';
import { BelowMapTab } from '../layouts/BelowMapTab';
import { ContractTable } from './ContractTable';
import { useContracts } from '../hooks/useContracts';
import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import { CloseIcon } from '@iconicicons/react';
import toast from 'react-hot-toast';
import { ReusableModal } from '../modals/ReusableModal';
import { ContractForm } from './ContractForm';
import {
  useCreateUpdateContractMutation,
  useGetOrganizationsQuery,
  useGetTSPsQuery,
} from '../services/edgeApi';
import { Contract } from '@/types';
import { NestedMessage } from '../helpers/NestedMessage';
import { useSelector } from 'react-redux';
import { selectTspOptions } from '../map/slice/mapSelectors';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { ContractTypeNumber } from '../types/api/Contract';
import { Rates } from '../rates/Rates';
import { useLocation } from 'react-router-dom';

export const Contracts = () => {
  useGetTSPsQuery(undefined);
  const { data: userOrganizations } = useGetOrganizationsQuery({
    mineOnly: true,
  });
  const [searchText, setSearchText] = useState<string>('');
  const [isAddingContract, setIsAddingContract] = useState<boolean>(false);
  const [baseContract, setBaseContract] = useState<Contract | null>(null);
  const [formIssues, setFormIssues] = useState<Object>({});
  const [filterTspIds, setFilterTspIds] = useState<number[]>([]);
  const [filterOrganizationIds, setFilterOrganizationIds] = useState<number[]>(
    [],
  );
  const [filterTypes, setFilterTypes] = useState<number[]>([]);
  const [filterUseInScenarios, setFilterUseInScenarios] = useState<
    boolean | undefined
  >(undefined);
  const [filterIsActiveOnDate, setFilterIsActiveOnDate] = useState<
    boolean | undefined
  >(undefined);
  const { contracts, isLoading, isFetching, error, exhausted, next, refresh } =
    useContracts({
      includeDetails: true,
      pageSize: 100,
      filters: {
        searchText,
        types: filterTypes,
        organizationIds: filterOrganizationIds,
        tspIds: filterTspIds,
        useInScenarios: filterUseInScenarios,
        isActiveOnDate: filterIsActiveOnDate,
      },
    });
  const [createUpdateContract, {}] = useCreateUpdateContractMutation();
  const tspOptions = useSelector(selectTspOptions);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editContractId = params.get('edit_contract');
    if (editContractId === 'new') {
      setBaseContract(null);
      setIsAddingContract(true);
    }
  }, [location.search]);

  const contractsArray = useMemo(
    () => Object.values(contracts ?? {}),
    [contracts],
  );

  useEffect(() => {
    if (error) {
      toast.error(`Error fetching contracts: ${error}`);
    }
  }, [error]);

  const organizationOptions = useMemo(
    () =>
      userOrganizations?.map((org) => ({
        value: org.id.toString(),
        label: org.name,
      })) ?? [],
    [userOrganizations],
  );
  const contractTypeOptions = useMemo(
    () => [
      { value: ContractTypeNumber.FIRM, label: 'FIRM' },
      { value: ContractTypeNumber.IT, label: 'IT' },
    ],
    [],
  );

  const closeContractForm = () => {
    setIsAddingContract(false);
    setBaseContract(null);
    setFormIssues({});
  };

  return (
    <>
      <BelowMapSetter>
        <BelowMapTab tabName="Contracts" contextKey={'contracts'}>
          <div className="m-3 d-flex gap-3 justify-content-between align-items-center">
            <div className="d-flex gap-2 flex-column">
              <div className="input-group">
                <label className="input-group-text">Search</label>
                <input
                  className="form-control"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className="d-flex gap-2">
                <div style={{ width: '200px' }}>
                  <MultiSelectSearch
                    placeholder="Contract Type"
                    options={contractTypeOptions}
                    onChange={(selected) => {
                      setFilterTypes(selected.map((s) => s.value));
                    }}
                  />
                </div>
                <div style={{ width: '200px' }}>
                  <MultiSelectSearch
                    placeholder="Owner"
                    options={organizationOptions}
                    onChange={(selected) => {
                      setFilterOrganizationIds(
                        selected.map((s) => parseInt(s.value)),
                      );
                    }}
                  />
                </div>
                <div style={{ width: '250px' }}>
                  <MultiSelectSearch
                    placeholder="Pipelines"
                    options={tspOptions}
                    onChange={(selected) => {
                      setFilterTspIds(selected.map((s) => s.value));
                    }}
                  />
                </div>
              </div>
              <div className="d-flex gap-3 align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <label htmlFor="filterUseInScenarios" className="mb-0">
                    Use In Scenarios:
                  </label>
                  <select
                    id="filterUseInScenarios"
                    className="form-select form-select-sm"
                    style={{ width: '120px' }}
                    value={
                      filterUseInScenarios === undefined
                        ? 'all'
                        : filterUseInScenarios
                        ? 'true'
                        : 'false'
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setFilterUseInScenarios(
                        val === 'all' ? undefined : val === 'true',
                      );
                    }}
                  >
                    <option value="all">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <label
                    htmlFor="filterIsActive"
                    className="mb-0"
                    title="Is Active checks if Use in Scenarios is true and if the current date is within the contract's date range"
                  >
                    Is Active:
                    <small
                      className="text-muted ms-1"
                      style={{ fontSize: '0.75em' }}
                    >
                      (use in scenarios + in date range)
                    </small>
                  </label>
                  <select
                    id="filterIsActive"
                    className="form-select form-select-sm"
                    style={{ width: '120px' }}
                    value={
                      filterIsActiveOnDate === undefined
                        ? 'all'
                        : filterIsActiveOnDate
                        ? 'true'
                        : 'false'
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setFilterIsActiveOnDate(
                        val === 'all' ? undefined : val === 'true',
                      );
                    }}
                  >
                    <option value="all">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>
            </div>
            <div>
              <button
                className="btn btn-success"
                onClick={() => setIsAddingContract(true)}
              >
                Create Contract
              </button>
            </div>
          </div>
          <ContractTable
            contracts={contractsArray}
            visibleContracts={{}}
            setVisibility={() => null}
            showFilters={false}
            isLoading={isLoading || isFetching}
            isEmpty={false}
            isEditable={true}
            onEditContract={(contract) => {
              setBaseContract(contract);
              setIsAddingContract(true);
            }}
          />
          <InfiniteScrollObserver
            onScrollIn={() => {
              if (exhausted || error || isLoading || isFetching) return;
              next();
            }}
          />
        </BelowMapTab>
        <BelowMapTab tabName="Rates" contextKey="rates-table">
          <Rates />
        </BelowMapTab>
      </BelowMapSetter>
      <ReusableModal
        size="xl"
        isOpen={isAddingContract}
        onClose={closeContractForm}
      >
        <div className="mb-4">
          <h4 className="modal-title text-center align-items-center">
            {baseContract
              ? `Edit Contract ${baseContract.k_id}`
              : 'New Contract'}
            <span role="button" className="float-end">
              <CloseIcon onClick={closeContractForm} />
            </span>
          </h4>
        </div>
        <ContractForm
          baseContract={baseContract ?? undefined}
          onCreate={async (contract) => {
            try {
              await createUpdateContract(
                baseContract ? { ...contract, id: baseContract.id } : contract,
              ).unwrap();
              toast.success(`Contract created successfully.`);
              refresh();
              closeContractForm();
            } catch (err: any) {
              console.error('Error creating contract:', err);
              if ('data' in err) {
                setFormIssues(err.data);
              } else {
                toast.error(
                  'An unexpected error occurred while creating the contract.',
                );
              }
              return;
            }
          }}
        />
        <div className="mt-3 d-flex flex-column gap-1 float-end">
          {Object.keys(formIssues).length > 0 && (
            <NestedMessage message={formIssues} level="danger" />
          )}
        </div>
      </ReusableModal>
    </>
  );
};
